import { useState, useEffect, useRef } from 'react';
import { BoothCard, BoothButton } from '@photobooth/ui';
import QRCode from 'qrcode';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../../../lib/config/terminal-urls';
import type { LocalProduct } from '../../../lib/db/schema';
import type { CustomerInfo } from './CustomerForm';

interface PaymentScreenProps {
  product: LocalProduct;
  customerInfo: CustomerInfo;
  transactionId: string;
  voucherCode?: string;
  onSuccess: (transactionId: string) => void;
  onBack: () => void;
}

type PaymentStatus = 'pending' | 'completed' | 'failed' | 'expired';

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

export function PaymentScreen({ product, customerInfo, transactionId, onSuccess, onBack }: PaymentScreenProps) {
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('pending');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    startPaymentPolling(transactionId);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [transactionId]);

  function startPaymentPolling(txnId: string) {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await apiClient.transactions.getById(txnId);
        if (response.success && response.data) {
          const txn = response.data as { payment?: { status: string }; paymentStatus?: string };
          const status = txn.payment?.status ?? txn.paymentStatus;
          if (status === 'completed') {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            setPaymentStatus('completed');
            await generateTransactionQR(txnId);
          }
        }
      } catch (err) {
        console.warn('Payment poll error:', err);
      }
    }, 3000);
  }

  async function generateTransactionQR(txnId: string) {
    try {
      const qrUrl = await QRCode.toDataURL(txnId, {
        width: 400,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error('Failed to generate transaction QR:', err);
    }
  }

  function handleRefresh() {
    if (transactionId) {
      apiClient.transactions.getById(transactionId).then(async (response) => {
        if (response.success && response.data) {
          const txn = response.data as { payment?: { status: string } };
          const status = txn.payment?.status ?? (response.data as { paymentStatus?: string }).paymentStatus;
          if (status === 'completed') {
            setPaymentStatus('completed');
            await generateTransactionQR(transactionId);
          }
        }
      });
    }
  }

  function handleComplete() {
    onSuccess(transactionId);
  }

  if (paymentStatus === 'completed') {
    return (
      <BoothCard className="max-w-2xl mx-auto">
        <div className="p-8 text-center">
          <div className="text-8xl mb-6 animate-bounce">✅</div>
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">
            Payment Successful!
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Please scan this QR code at Capture
          </p>

          {qrCodeUrl && (
            <div className="bg-white p-6 rounded-3xl shadow-lg inline-block mb-6">
              <img src={qrCodeUrl} alt="Transaction QR Code" className="w-80 h-80" />
            </div>
          )}

          <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6 mb-6">
            <p className="text-lg font-semibold text-gray-800 mb-2">Transaction ID</p>
            <p className="text-2xl font-mono font-bold text-gray-900">{transactionId}</p>
          </div>

          <BoothButton onClick={handleComplete} size="lg" className="w-full">
            Done
          </BoothButton>
        </div>
      </BoothCard>
    );
  }

  return (
    <BoothCard className="max-w-2xl mx-auto">
      <div className="p-8">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🏪</div>
          <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            Bayar di Kasir
          </h2>
          <p className="text-gray-600">
            Silakan bayar ke kasir dengan nomor order berikut
          </p>
        </div>

        <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 mb-6">
          <div className="bg-white rounded-2xl p-6 mb-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Nomor Order</p>
            <p className="text-2xl font-mono font-bold text-gray-900 break-all">{transactionId || '...'}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 mb-6 text-center">
            <p className="text-sm text-gray-600 mb-2">Amount to Pay</p>
            <p className="text-4xl font-bold text-gray-900">
              Rp {product.price.toLocaleString('id-ID')}
            </p>
          </div>

          <p className="text-center text-gray-600 text-sm">
            Setelah membayar, kasir akan menyetujui pembayaran. Tekan Refresh atau tunggu beberapa detik.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-gray-700">
            <span className="text-2xl">📦</span>
            <span>{product.name}</span>
          </div>
          <div className="flex items-center gap-3 text-gray-700">
            <span className="text-2xl">👤</span>
            <span>{customerInfo.name}</span>
          </div>
          <div className="flex items-center gap-3 text-gray-700">
            <span className="text-2xl">📧</span>
            <span>{customerInfo.email}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <BoothButton onClick={handleRefresh} variant="outline" className="flex-1" size="lg">
            Refresh
          </BoothButton>
          <BoothButton onClick={onBack} variant="outline" className="flex-1" size="lg">
            Cancel
          </BoothButton>
        </div>
      </div>
    </BoothCard>
  );
}
